import _ from 'lodash';  // Import lodash for debouncing
import { apiFetch } from "apiClient"; // adjust path
import { apiFetch as fetch} from "apiClient";
import {useContext} from "react";
import {DataContext} from "@dashboard/contexts/DataContext"; // adjust path

export const getTabList = async () => {
    try {
        const res  = await fetch('/api/gui-instruments/gui-instruments-tab-list');
        const data = await res.json();
        return data;
    } catch (err) {
        console.error('Error fetching tab list:', err);
        return [];
    }
};

export const updateTickerSubscriptionWithKite = async (GUIInstruments, tabID) => {
    if (!tabID) {
        throw new Error("updateTickerSubscriptionWithKite: tabID is required.");
    }

    // Normalize input into an array
    const instrumentsArray = Array.isArray(GUIInstruments)
        ? GUIInstruments
        : [GUIInstruments];

    if (instrumentsArray.length === 0) {
        return new Response(
            JSON.stringify({ message: 'No GUIInstruments to subscribe' }),
            { status: 200 }
        );
    }

    const instrumentTokens = instrumentsArray
        .map(i => i.instrumentToken)
        .filter(Boolean); // remove null/undefined

    if (instrumentTokens.length === 0) {
        return new Response(
            JSON.stringify({ message: 'No valid instrument tokens' }),
            { status: 400 }
        );
    }

    // --- API CALL ---
    const res = await fetch(
        `/api/gui-instruments/kite/start-subscription-withkite/${tabID}/${instrumentTokens.join(',')}`,
        { method: 'POST' }
    );

    // --- SET DETAIL ACCESS OWNER (Only for visible rows) ---
    const visible = instrumentsArray.filter(i => i.isDetailsVisible === true);

    await Promise.all(
        visible.map(i => {
            if (!i.instrumentToken) {
                console.warn(`Skipping invalid instrument token:`, i);
                return null;
            }
            return setSubscriptionToDetails(i.instrumentToken, `tab:${tabID}`);
        })
    );

    return res;
};


export const updateTokenSubscriptionWithKite = async (instrumentTokens, tabID) => {
    if (!tabID) {
        throw new Error("updateTokenSubscriptionWithKite: tabID is required.");
    }

    if (!instrumentTokens || instrumentTokens.length === 0) {
        return new Response(JSON.stringify({ message: 'No instrumentTokens to subscribe' }), { status: 200 });
    }

    return fetch(
        `/api/gui-instruments/kite/start-subscription-withkite/${tabID}/${instrumentTokens.join(',')}`,
        { method: 'POST' }
    );
};


// ---------- Shared subscription mode helpers ----------

export const setSubscriptionToDetails = async (instrumentToken, owner) => {
    if (!owner) {
        throw new Error(`setSubscriptionToDetails: owner is required for token ${instrumentToken}`);
    }

    return fetch(`/api/gui-instruments/kite/detailsSubscription/${instrumentToken}`, {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ owner })
    });
};


export const setSubscriptionToQuote = async (instrumentToken, owner) => {
    if (!owner) {
        throw new Error(`setSubscriptionToQuote: owner is required for token ${instrumentToken}`);
    }

    return fetch(`/api/gui-instruments/kite/quoteSubscription/${instrumentToken}`, {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ owner })
    });
};

export const getExactInstrument = (query, allInstrumentsList) => {
    if (!query) return null;
    query=query.replace(/-BE$/, "");
    // Find all exact matches
    const exactMatches = allInstrumentsList.filter(instrument =>
        instrument &&
        ((instrument.tradingsymbol && instrument.tradingsymbol.toLowerCase() === query.toLowerCase()) ||
            (instrument.name && instrument.name.toLowerCase() === query.toLowerCase()))
    );

    if (exactMatches.length === 0) {
        return null; // No match found
    }

    // Check for invalid exchange values
    const invalidExchange = exactMatches.find(instrument => instrument.exchange !== "NSE" && instrument.exchange !== "BSE");
    if (invalidExchange) {
        throw new Error(`Invalid exchange found for instrument: ${JSON.stringify(invalidExchange)}`);
    }

    // Prioritize NSE, then BSE
    const nseMatch = exactMatches.find(instrument => instrument.exchange === "NSE");
    if (nseMatch) return nseMatch;

    const bseMatch = exactMatches.find(instrument => instrument.exchange === "BSE");
    if (bseMatch) return bseMatch;

    return null; // This line should never be reached unless all matches had invalid exchanges
};

export const saveArrayToFile = (dataArray, fileName = 'data.json') => {
    const jsonString = JSON.stringify(dataArray, null, 2); // Pretty print (optional)
    const blob = new Blob([jsonString], { type: 'application/json' });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Cleanup
};

// Find a single instrumentToken for a given tradingsymbol + exchange ("NSE" | "BSE")
export const findInstrumentTokenForExchange = (tradingsymbol, exchange, allInstrumentsList) => {
    if (!tradingsymbol || !exchange || !Array.isArray(allInstrumentsList)) {
        return null;
    }

    const tsLower = tradingsymbol.toLowerCase();
    const exUpper = exchange.toUpperCase();

    // exact match on tradingsymbol + exchange
    const matches = allInstrumentsList.filter(inst =>
        inst &&
        inst.tradingsymbol &&
        inst.exchange &&
        inst.tradingsymbol.toLowerCase() === tsLower &&
        inst.exchange.toUpperCase() === exUpper
    );

    if (matches.length === 0) {
        return null;
    }

    // If multiple, prefer EQ segment if available
    const eqMatch = matches.find(m => m.instrumentType === 'EQ' || m.segment === exUpper);
    const chosen = eqMatch || matches[0];

    return chosen.instrumentToken || null;
};


export const handleSearch = _.debounce((query, allInstrumentsList, setFilteredInstruments, allExchangeInstruments = []) => {
    if (!query || query.length <= 2) {
        setFilteredInstruments([]);
        return;
    }

    const q = query.toLowerCase();

    // --------------------
    // 1) Primary search: AllInstrumentsList (InstrumentEntity)
    // --------------------
    const primaryByTradingSymbol = (allInstrumentsList || []).filter(instrument =>
        instrument && instrument.tradingsymbol &&
        instrument.tradingsymbol.toLowerCase().includes(q)
    );

    const primaryByName = (allInstrumentsList || []).filter(instrument =>
        instrument && instrument.name &&
        instrument.name.toLowerCase().includes(q)
    );

    // Combine and remove duplicates by object identity (same as your old logic)
    const primaryCombined = [...new Set([...primaryByTradingSymbol, ...primaryByName])];

    // Build de-dupe sets from primary results (case-insensitive)
    const primarySymbols = new Set(
        primaryCombined
            .map(i => (i?.tradingsymbol || '').toLowerCase())
            .filter(Boolean)
    );

    const primaryNames = new Set(
        primaryCombined
            .map(i => (i?.name || '').toLowerCase())
            .filter(Boolean)
    );

    // --------------------
    // 2) Secondary search: allExchangeInstruments (exchange+tradingsymbol+name)
    //    Only include items whose symbol OR name is NOT already present in primary results.
    // --------------------
    const secondarySource = Array.isArray(allExchangeInstruments) ? allExchangeInstruments : [];

    const secondaryMatchesRaw = secondarySource.filter(item => {
        const ts = (item?.tradingsymbol || '').toLowerCase();
        const nm = (item?.name || '').toLowerCase();
        return (ts && ts.includes(q)) || (nm && nm.includes(q));
    });

    // Exclude if primary already has same symbol OR same name
    const secondaryFiltered = [];
    const seenSecondaryKey = new Set();

    for (const item of secondaryMatchesRaw) {
        const ts = (item?.tradingsymbol || '').toLowerCase();
        const nm = (item?.name || '').toLowerCase();

        if (!ts && !nm) continue;
        if (ts && primarySymbols.has(ts)) continue;      // ignore symbol duplicates
        if (nm && primaryNames.has(nm)) continue;        // ignore name duplicates

        // Avoid duplicates inside secondary list as well
        const ex = (item?.exchange || '').toUpperCase();
        const key = `${ex}|${ts}|${nm}`;
        if (seenSecondaryKey.has(key)) continue;
        seenSecondaryKey.add(key);

        secondaryFiltered.push(item);
    }

    // --------------------
    // 3) Combine + sort (keep your existing "startsWith" priority)
    // --------------------
    const combined = [...primaryCombined, ...secondaryFiltered];

    const sortedFiltered = combined.sort((a, b) => {
        const aName = (a?.name || '').toLowerCase();
        const bName = (b?.name || '').toLowerCase();
        const aTs = (a?.tradingsymbol || '').toLowerCase();
        const bTs = (b?.tradingsymbol || '').toLowerCase();

        const aStarts = aTs.startsWith(q) || aName.startsWith(q);
        const bStarts = bTs.startsWith(q) || bName.startsWith(q);

        if (aStarts && !bStarts) {
            return -1;
        }
        if (!aStarts && bStarts) {
            return 1;
        }
        return 0;
    });

    console.log("Setting filtered instruments to", sortedFiltered);
    setFilteredInstruments(sortedFiltered);
}, 300);  // Delay the search by 300ms

// Fetch all holdings from the server
export const getHoldings = async () => {
    try {
        const res  = await fetch(`/api/kite/holdings`);
        const data = await res.json();
        return Array.isArray(data) ? data : [];
    } catch (err) {
        console.error('Error fetching holdings:', err);
        return [];
    }
};

// Get holding quantity by tradingsymbol and exchangeName
export const getPortfolioHoldingQuantity = async (tradingsymbol, exchange) => {
    try {
        const res  = await fetch(`/api/kite/holdings/portfolioholdings/${tradingsymbol}/quantity`);
        if (!res || res.status === 204) {
            return null;
        }
        const data = await res.json();
        return data ?? null; // keep old behavior: null if missing
    } catch (err) {
        console.error('Error fetching holding quantity:', err);
        return null;
    }
};

export const fetchPositions = async () => {
    try {
        const res  = await fetch('/api/tracked-stocks/all');
        const data = await res.json();
        return data ?? null;
    } catch (err) {
        console.error('Error fetching positions:', err);
        return null;
    }
};

export const updateGUIInstrumentsToBackend = async (GUIInstruments, tabID) => {
    try {
        const res = await fetch(
            `/api/gui-instruments/update-all-gui-instruments?tabID=${encodeURIComponent(tabID)}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(GUIInstruments),
            }
        );
        // Optional: parse if server returns JSON
        // const data = await res.json();
        // console.log('Response:', data);
        return res; // raw Response; check res.ok / res.status at call site if needed
    } catch (error) {
        console.error('Error updating GUI instruments:', error);
        throw error;
    }
};

export const addPosition = async (position) => {
    try {
        const res = await fetch('/api/tracked-stocks/add', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(position),
        });
        // const data = await res.json(); console.log('Position saved:', data);
        return res;
    } catch (error) {
        console.error('Error saving position:', error);
        throw error;
    }
};

export const updateAllPositions = async (positions) => {
    try {
        const res = await fetch('/api/tracked-stocks/updateAll', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(positions),
        });
        // const data = await res.json(); console.log('Positions saved:', data);
        return res;
    } catch (error) {
        console.error('Error saving positions:', error);
        throw error;
    }
}
export const handleTradingsymbolClick = (tradingsymbol, screenerURLs) => {
    if (tradingsymbol.endsWith("-SM")) {
        tradingsymbol = tradingsymbol.replace("-SM", "");
    }

    const url = screenerURLs?.[tradingsymbol] || "https://www.screener.in/";
    window.open(url, "_blank");
}

